import {
    isEmploymentStatusDataComplete,
    isIncomeDataComplete,
    isInsuranceDataComplete,
    isKidsDataComplete,
    isTaxDataComplete,
    type MembershipProfileKidAnswer,
    QuestionKey,
} from '@fruitful/member-profile';

import type { User } from '../../user/types/User';
import useGetMembershipIncomes from './useGetMembershipIncomes';
import useGetMembershipProfileAnswers from './useGetMembershipProfileAnswers';

export interface UserFinancialProfileCompleteness {
    isLoading: boolean;
    isEmploymentComplete: boolean;
    isTaxComplete: boolean;
    isInsuranceComplete: boolean;
    isIncomeComplete: boolean;
    isKidsComplete: boolean;
    isFinancialProfileComplete: boolean;
}

// Checks financial profile completeness at the MEMBER level, mostly consumed in Settings contexts, versus membership level completeness hooks that are mostly consumed in onboarding contexts.
export const useGetUserFinancialProfileCompleteness = (
    user: User | null,
): UserFinancialProfileCompleteness => {
    const { data: membershipIncomes, isLoading: isLoadingMembershipIncomes } =
        useGetMembershipIncomes({});

    // Don't include category param on these calls to be able to hit the query cache
    const { data: kids, isLoading: isLoadingKidsAnswer } =
        useGetMembershipProfileAnswers<MembershipProfileKidAnswer>({
            questionKey: QuestionKey.KIDS,
        });

    if (user == null) {
        return {
            isLoading: false,
            isEmploymentComplete: false,
            isInsuranceComplete: false,
            isTaxComplete: false,
            isKidsComplete: false,
            isIncomeComplete: false,
            isFinancialProfileComplete: false,
        };
    }

    const isLoading = isLoadingKidsAnswer || isLoadingMembershipIncomes;

    const isEmploymentComplete = isEmploymentStatusDataComplete(user, null);
    const isInsuranceComplete = isInsuranceDataComplete(user, null);
    const isTaxComplete = isTaxDataComplete(user, null);
    const isKidsComplete = isKidsDataComplete(kids);
    const isIncomeComplete = isIncomeDataComplete(
        user,
        null,
        membershipIncomes ?? [],
    );
    const isFinancialProfileComplete =
        isEmploymentComplete &&
        isInsuranceComplete &&
        isTaxComplete &&
        isKidsComplete &&
        isIncomeComplete;

    return {
        isLoading,
        isEmploymentComplete,
        isInsuranceComplete,
        isTaxComplete,
        isKidsComplete,
        isIncomeComplete,
        isFinancialProfileComplete,
    };
};
